import styled from '@emotion/styled'
import { round } from 'lodash'
import { ReactNode } from 'react'

import { Color, theme } from '../../../constants'
import { Clickable, Div, Flex, Text } from '../../../elements'

export const OverviewCalendarRowTitle = styled(Clickable)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: `${theme.spacing[1]}px ${theme.spacing[2]}px`,
  borderRadius: theme.border.radius.small,
  ':hover': {
    backgroundColor: Color.LIGHT_GREY,
  },
})

type RowType = 'UNIT' | 'TENDER' | 'BOOKING' | 'HEADER'

type OverviewCalendarRowProps = {
  title: ReactNode
  type: RowType
  weeks: (null | string | number)[]
  isNested?: boolean
}

const Row = styled(Flex)<{ isNested: boolean }>(
  ({ isNested }: { isNested: boolean }) => ({
    gap: theme.spacing[3],
    padding: `${theme.spacing[1]}px ${theme.spacing[1.5]}px`,
    alignItems: 'center',
    alignContent: 'center',
    borderBottom: `1px solid ${Color.MEDIUM_GREY}`,
    background: isNested ? Color.BACKGROUND_GREY : Color.WHITE,
    ':last-child': {
      borderBottom: 'none',
    },
  }),
)

const WeekCell = styled(Flex)<{ background: Color; color?: Color }>(
  ({ background, color }) => ({
    background,
    color,
    borderRadius: theme.border.radius.small,
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: theme.font.weight.bold,
    userSelect: 'none',
  }),
)

export const OverviewCalendarRow = (props: OverviewCalendarRowProps) => {
  const isBooking = props.type === 'BOOKING'

  return (
    <Row isNested={!!props.isNested}>
      {props.title}
      <Div
        styles={{
          display: 'grid',
          gridTemplateColumns: `repeat(${props.weeks.length}, 80px)`,
          gridTemplateRows: '34px',
          gridGap: theme.spacing[2],
          height: '100%',
          ml: 'auto',
        }}
      >
        {props.weeks.map((content, index) => {
          if (props.type === 'HEADER') {
            return (
              <WeekCell
                key={index}
                background={Color.TRANSPARENT}
                color={Color.DARK_GREY}
              >
                v.{content}
              </WeekCell>
            )
          }

          if (content === null) {
            return <WeekCell key={index} background={Color.TRANSPARENT} />
          }

          if (!content) {
            return (
              <WeekCell
                key={index}
                background={isBooking ? Color.LIGHTER_GREEN : Color.LIGHT_GREY}
                color={isBooking ? Color.LIGHT_GREEN : Color.LIGHTER_BLUE}
              >
                0
              </WeekCell>
            )
          }

          return (
            <WeekCell
              key={index}
              background={isBooking ? Color.GREEN : Color.LIGHTEST_BLUE}
              color={isBooking ? Color.WHITE : Color.DARK_BLUE}
            >
              <Text fontSize={theme.font.size[6]}>
                {typeof content === 'number' ? round(content) : content}
              </Text>
            </WeekCell>
          )
        })}
      </Div>
    </Row>
  )
}
